import {QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {connectionURL} from 'instruments/connections/ConnectionDAO';
import * as navigation from 'Navigation';

import {ConnectionQuery, ConnectionQueryVariables} from './__generated/ConnectionQuery';
import ConnectionDataTabState from './ConnectionDataTabState';
import {CONNECTION_QUERY} from './ConnectionDetails';
import ConnectionDetailsTabState from './ConnectionDetailsTabState';
import ConnectionRunsTabState from './ConnectionRunsTabState';

type Connection = ExtractByTypename<ConnectionQuery['connection'], 'ConnectionNode'>;

interface ConnectionFileStateAttributes {
  connection?: Connection;
  name?: string;
}

class ConnectionFileState extends ApolloFileState<
  ConnectionQuery,
  ConnectionQueryVariables,
  ConnectionFileStateAttributes
> {
  static fileType = ModelTypeConstants.CONNECTION;
  static openFile(fileId: string): JQueryPromise<ConnectionFileState> {
    return super.openFile(fileId) as JQueryPromise<ConnectionFileState>;
  }

  static getQueryOptions(fileId: string): QueryOptions<ConnectionQueryVariables> {
    return {
      query: CONNECTION_QUERY,
      variables: {
        connectionId: fileId,
      },
    };
  }

  static dataToAttributes(data: ConnectionQuery): ConnectionFileStateAttributes {
    const {connection} = data;
    return isInaccessible(connection) ? {} : {connection, name: connection.name};
  }

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
    };
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  initialize(...args: any): void {
    super.initialize(...args);
    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    const buffer = buffers.at(0);
    buffer.tabs().add(new ConnectionDetailsTabState({connectionId: this.id}));
    buffer.tabs().at(0).set({active: true});
    buffer.tabs().add(new ConnectionRunsTabState({connectionId: this.id}));
    buffer.tabs().add(new ConnectionDataTabState({connectionId: this.id}));
  }

  editURL(): string {
    return connectionURL(this.id);
  }

  name(): string {
    return this.get('name') ?? 'Loading...';
  }

  registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
  }
}

export default ConnectionFileState;
